@if (displayFileUploaded) {
  <div class="file_list">
    @if (uploadedFiles.length === 0) {
      <div class="fileUpload-empty" [style.font-size]="overridedCSS?.inputFontSize">
        @if (isLoaderActive) {
          <p-progressSpinner />
        } @else {
          {{ uploadedFileList_noFiles | translate }}
        }
      </div>
    } @else {
      <ng-container
        [ngTemplateOutlet]="fileTable"
        [ngTemplateOutletContext]="{
          tableClass: 'uploadedFiles',
          ariaLabel: 'uploaded files table',
          title: uploadedFileList_title,
          src: 'files',
          cols: getTagColumns(uploadedFiles, 'files'),
          files: uploadedFiles | filterList: filterListOptions,
        }" />
    }
  </div>

  @if (linkedFiles.length > 0) {
    <div class="file_list linked_file" data-cy="fileupload-linkedfile-list">
      <ng-container
        [ngTemplateOutlet]="fileTable"
        [ngTemplateOutletContext]="{
          tableClass: 'linkedFiles',
          ariaLabel: 'linked files table',
          title: linkedFileList_title,
          src: 'linkedFiles',
          cols: getTagColumns(linkedFiles, 'linkedFiles'),
          files: linkedFiles | filterList: filterLinkedListOptions,
        }" />
    </div>
  }
}

<iu-prime-overlay
  #op
  appendTo="body"
  baseZIndex="2000"
  styleClass="fileUpload-overlayPanel"
  [showCloseIcon]="true"
  [style]="{ width: '450px' }">
  <ng-template pTemplate>
    <p-table [paginator]="true" [rows]="5" [value]="selectedOverflowTags">
      <ng-template pTemplate="header">
        <tr>
          <th scope="col">{{ 'fileupload.tagName' | translate }}</th>
          <th scope="col">{{ 'fileupload.tagValue' | translate }}</th>
        </tr>
      </ng-template>
      <ng-template let-displayedTag let-rowData pTemplate="body">
        <tr [pSelectableRow]="rowData">
          <td>{{ getTagLabel(displayedTag) }}</td>
          <td>
            {{ getTagValue(selectedFile, displayedTag) | docServerFormatValue: displayedTag.ad_reference_id }}
          </td>
        </tr>
      </ng-template>
    </p-table>
  </ng-template>
</iu-prime-overlay>

@if (isReadOnly || canUpload) {
  <div class="fileUpload-blocks">
    <!-- block 1: add local files (drop zone + pending files + upload) -->
    <section class="fileUpload-block" data-cy="fileupload-block-files">
      <h3 class="upload-list-title" style="margin-top: 0px">
        <i aria-hidden="true" class="fas fa-file-arrow-up"></i>
        {{ uploadingFileList_title | translate }}
      </h3>
      <div class="fileUpload-block-body">
        <div [ngClass]="['ui-fileupload', styleClass || '']" [ngStyle]="style">
          <p-messages [enableService]="false" [value]="msgs" />
          <div
            #content
            data-cy="fileupload-click-or-drop-zone"
            [ngClass]="['ui-fileupload-content ui-corner-bottom']"
            (click)="addFile($event)"
            (dragenter)="onDragEnter($event)"
            (dragleave)="onDragLeave($event)"
            (drop)="onDrop($event)">
            <input
              #advancedfileinput
              data-cy="fileupload-input-file"
              style="display: none"
              type="file"
              [id]="inputId()"
              [multiple]="multiple"
              [readonly]="isReadOnly"
              [tabindex]="isReadOnly ? '-1' : null"
              (blur)="onBlur()"
              (change)="onFileSelect($event)"
              (focus)="!isReadOnly ? onFocus() : null" />
            <div class="ui-fileupload-label">
              <i aria-hidden="true" class="fas fa-cloud-upload-alt"></i>
              <p>{{ uploadingFileList_drag | translate }}</p>
            </div>
          </div>

          @if (hasFiles()) {
            <div class="fileUpload-pending" data-cy="fileupload-pending-files">
              <div class="fileUpload-pending-header">
                <span class="fileUpload-pending-title">
                  <i aria-hidden="true" class="icon-hours"></i>
                  {{ 'fileupload.pending_files' | translate: { count: files.length } }}
                </span>
                @if (!auto && displayButtons) {
                  <button
                    class="p-button p-button-default no-background no-border fileUpload-clearBtn"
                    data-cy="fileupload-btn-cancel"
                    icon="icon-close"
                    pButton
                    type="button"
                    [disabled]="isReadOnly || uploadingFiles.length > 0"
                    label="{{ uploadBtn_cancel | translate }}"
                    (click)="clear()"></button>
                }
              </div>
              <table aria-label="files upload table" class="ui-fileupload-table">
                @for (file of files; track $index; let i = $index) {
                  <tr>
                    <td class="fileUpload-logo-cell">
                      @if (uploadingFiles.indexOf(file) !== -1) {
                        <p-progressSpinner />
                      } @else {
                        <ng-container
                          [ngTemplateOutlet]="fileLogo"
                          [ngTemplateOutletContext]="{
                            fileItem: file | fileUploadLogoFile: fromDocServer : isImage,
                            file: file,
                            i: i,
                          }" />
                      }
                    </td>
                    <th scope="row">
                      <div
                        class="prime-fileupload-filename"
                        [style.font-size]="overridedCSS?.inputFontSize"
                        [title]="file.name">
                        {{ file.name }}
                      </div>
                    </th>
                    <td class="fileUpload-pending-tags">
                      <iu-tags-editor-ui [(tags)]="tags[i]" />
                    </td>
                    <td class="fileUpload-row-actions">
                      @if (uploadingFiles.indexOf(file) === -1) {
                        <button
                          class="fileUpload-action fileUpload-action-delete"
                          icon="icon-close"
                          pButton
                          tooltipPosition="top"
                          type="button"
                          [attr.aria-label]="'generic.delete' | translate"
                          [pTooltip]="'generic.delete' | translate"
                          (click)="remove($event, i)"></button>
                      }
                    </td>
                  </tr>
                }
              </table>
              @if (!auto && displayButtons) {
                <p class="fileUpload-pending-hint">
                  {{ 'fileupload.pending_hint' | translate: { button: (uploadBtn_confirm | translate) } }}
                </p>
              }
            </div>
          }
        </div>
        @if (!auto) {
          <div class="ui-fileupload-button-container">
            @if (displayButtons) {
              <button
                class="p-button p-button-primary"
                data-cy="fileupload-btn-confirm"
                icon="fa fa-upload"
                pButton
                type="button"
                [disabled]="isReadOnly || uploadingFiles.length > 0 || !hasFiles()"
                label="{{ uploadBtn_confirm | translate }}"
                (click)="onClickUploadFiles()"></button>
            }
          </div>
        }
      </div>
    </section>

    <!-- block 2: attach an external link -->
    <section class="fileUpload-block" data-cy="fileupload-block-url">
      <h3 class="upload-list-title" style="margin-top: 0px">
        <i aria-hidden="true" class="fa fa-link"></i>
        {{ 'fileupload.attach_url_title' | translate }}
      </h3>
      <div class="fileUpload-block-body">
        <div class="ui-fileupload-url-input">
          <iu-input-text-ui
            #fileuploadUrlInput
            class="ui-fileupload-url-input-text"
            [isStandalone]="true"
            [placeHolder]="'fileupload.url_placeholder' | translate"
            (enterKey)="attachAnUrl($event, fileuploadUrlInput, fileuploadUrlTags)" />
          <iu-tags-editor-ui #fileuploadUrlTags class="ui-fileupload-url-input-tags" />
          <iu-button-ui
            btnType="secondary"
            data-cy="fileupload-btn-attach-url"
            icon="icon-add"
            [isStandalone]="true"
            [label]="'fileupload.attach_url' | translate"
            (click)="attachAnUrl($event, fileuploadUrlInput, fileuploadUrlTags)" />
        </div>
      </div>
    </section>
  </div>
}

<iu-preview-doc [fromDocServer]="fromDocServer" [modal]="true" />

<ng-template #fileLogo let-fileItem="fileItem" let-file="file" let-i="i">
  @if (fileItem && fileItem.isUrl && fileItem.isLocal) {
    @if (fileItem.url && !fileItem.failed) {
      <img
        alt="file preview"
        class="fileUpload-logo fileUpload-thumb"
        data-cy="previewFile"
        loading="lazy"
        [src]="fileItem.url"
        (click)="onPreview($event, file, i)"
        (error)="fileItem.failed = true" />
    } @else {
      <i aria-hidden="true" data-cy="previewFile" [ngClass]="['fileUpload-icon', fileItem.iconClass ?? '']"></i>
    }
  } @else if (fileItem && fileItem.isUrl) {
    <span class="fileUpload-thumb-holder" iuInViewport (inViewport)="loadThumb(fileItem.url)">
      @if (thumbs()[fileItem.url]?.src && !thumbs()[fileItem.url]?.failed) {
        <img
          alt="file preview"
          class="fileUpload-logo fileUpload-thumb"
          data-cy="previewFile"
          loading="lazy"
          [src]="thumbs()[fileItem.url].src"
          (click)="file?.provider === 'external' ? openLink($event, file.src) : onPreview($event, file, i)"
          (error)="onThumbError(fileItem.url)" />
      } @else {
        <i aria-hidden="true" data-cy="previewFile" [ngClass]="['fileUpload-icon', fileItem.iconClass ?? '']"></i>
      }
    </span>
  } @else if (fileItem) {
    <i
      alt="file preview"
      aria-hidden="true"
      data-cy="previewFile"
      [ngClass]="['fileUpload-icon', fileItem.iconClass ?? '']"></i>
  }
</ng-template>

<ng-template
  #fileTable
  let-ariaLabel="ariaLabel"
  let-cols="cols"
  let-files="files"
  let-src="src"
  let-tableClass="tableClass"
  let-title="title">
  <table [attr.aria-label]="ariaLabel" [ngClass]="tableClass">
    <thead>
      <tr class="file_list_header">
        <th colspan="2" scope="colgroup">
          <span [style.font-size]="overridedCSS?.labelFontSize">{{ title | translate }}</span>
        </th>

        <th class="fileUpload-col-date" scope="col">{{ fileList_createdDate | translate }}</th>

        @for (tag of cols.visible; track $index) {
          <th class="fileUpload-col-tag" scope="col">
            {{ getTagLabel(tag) }}
          </th>
        }

        <th scope="col">
          <input
            #filterInput
            class="file_upload_input_search"
            type="text"
            value=""
            width="10vw"
            [id]="inputId()"
            placeholder="{{ uploadedFileList_search | translate }}"
            (keyup)="updateFilterOptions($event, filterInput.value, src)" />
        </th>
      </tr>
    </thead>
    <tbody>
      <ng-container [ngTemplateOutlet]="tableBody" [ngTemplateOutletContext]="{ files: files, cols: cols }" />
    </tbody>
  </table>
</ng-template>

<ng-template #tableBody let-cols="cols" let-files="files">
  @for (file of files; track $index; let i = $index) {
    <tr data-cy="fileupload-linkedfile" [attr.data-cy-index]="i">
      <td>
        <ng-container
          [ngTemplateOutlet]="fileLogo"
          [ngTemplateOutletContext]="{ fileItem: file | fileUploadLogoFile: fromDocServer : isImage, file: file, i: i }" />
      </td>
      <td
        class="fileUpload-name"
        [style.font-size]="overridedCSS?.inputFontSize"
        [title]="file.name"
        (click)="file.provider === 'external' ? openLink($event, file.src) : onPreview($event, file, i)">
        {{ file.name }}
      </td>
      <td class="fileUpload-date">
        {{ file.createdDate }}
      </td>

      @for (tag of cols.visible; track $index) {
        <td class="taggedValue">
          @if (hasTagValue(file, tag)) {
            <span
              [ngClass]="isStatusTag(tag) ? 'fileUpload-badge' : 'fileUpload-tagText'"
              [title]="getTagValue(file, tag) | docServerFormatValue: tag.ad_reference_id">
              {{ getTagValue(file, tag) | docServerFormatValue: tag.ad_reference_id }}
            </span>
          }
        </td>
      }

      <td class="fileUpload-row-actions">
        @if (file.tags['META|SAE'] === 'Y') {
          @if (file.tags['META|ARKHINEO_ID']) {
            <a
              target="_blank"
              [href]="
                arkhineoBaseUrl +
                '/front/cfe/' +
                arkhineoCfeId +
                '/section/' +
                arkhineoSectionId +
                '/id/' +
                file.tags['META|ARKHINEO_ID']
              ">
              <button class="arkhineo-redirect-btn" pButton type="button" [title]="'arkhineo'">
                <img alt="arkhineo" src="assets/img/cropped-FAVICON-ARKHINEO-270x270.png" />
              </button>
            </a>
          } @else if (file.arkhineoTask) {
            <button
              class="arkhineo-redirect-btn disabled"
              pButton
              type="button"
              [title]="'arkhineo'"
              (click.single)="showTaskMessage(file.arkhineoTask)">
              <img alt="arkhineo" src="assets/img/cropped-FAVICON-ARKHINEO-270x270.png" />
            </button>
          }
        }
        @if (cols.overflow.length > 0) {
          <button
            class="fileUpload-action"
            icon="fas fa-info"
            pButton
            tooltipPosition="top"
            type="button"
            [attr.aria-label]="'fileupload.moreTags' | translate"
            [pTooltip]="'fileupload.moreTags' | translate"
            (click)="showMoreTagsOverlay($event, file, cols.overflow, op)"></button>
        }

        @if (file.provider !== 'external') {
          @if (canDownload) {
            <button
              class="fileUpload-action"
              icon="fa fa-download"
              pButton
              tooltipPosition="top"
              type="button"
              [attr.aria-label]="'generic.download' | translate"
              [pTooltip]="'generic.download' | translate"
              (click)="downloadFile($event, file, i)"></button>
          }
          <button
            class="fileUpload-action"
            data-cy="openFileOnSide"
            icon="fa fa-table-columns"
            pButton
            tooltipPosition="top"
            type="button"
            [attr.aria-label]="'generic.openFileOnSide' | translate"
            [pTooltip]="'generic.openFileOnSide' | translate"
            (click)="openFileOnSide($event, $any(file), i, files)"></button>
        } @else {
          <button
            class="fileUpload-action"
            data-cy="openInNewTab"
            icon="fas fa-up-right-from-square"
            pButton
            tooltipPosition="top"
            type="button"
            [attr.aria-label]="'generic.openInNewTab' | translate"
            [pTooltip]="'generic.openInNewTab' | translate"
            (click)="openLink($event, file.src)"></button>
        }
        <button
          class="fileUpload-action fileUpload-copyLink"
          data-cy="copyLinkFile"
          icon="fa fa-link"
          pButton
          tooltipPosition="top"
          type="button"
          [attr.aria-label]="'generic.copyLink' | translate"
          [pTooltip]="'generic.copyLink' | translate"
          (click)="copyLinkFile($event, file, i)"></button>
        @if (canDelete && (file.isDeletable === undefined || file.isDeletable)) {
          <!-- confirmation is asked by the parent (input-file-ui deleteFile) -->
          <button
            class="fileUpload-action fileUpload-action-delete"
            data-cy="deleteFile"
            icon="fa fa-trash"
            pButton
            tooltipPosition="top"
            type="button"
            [attr.aria-label]="'generic.delete' | translate"
            [disabled]="isReadOnly"
            [pTooltip]="'generic.delete' | translate"
            (click)="isReadOnly ? null : deleteFile($event, file, i)"></button>
        }
      </td>
    </tr>
  }
</ng-template>
